import { DeepPartial, ModelName } from "../../../types";
import { getObjectEntries } from "../../../utils/type-safe-object-functions";
import { TellescopeModelFromName } from "../../helper-types";
import { LoadFunctionArguments } from "@tellescope/sdk";
import { applyMongoFilter } from "@tellescope/utils";

export type GetSomeFilter<Model extends ModelName> = NonNullable<
  LoadFunctionArguments<TellescopeModelFromName<Model>>["filter"]
>;

export type GetSomeRequest<Model extends object> = LoadFunctionArguments<Model>;

/**
 * Determines whether a model object matches a given filter criteria.
 *
 * This function evaluates a model against filter conditions that can include:
 * - Simple equality checks (direct property values)
 * - Nested object filters (recursively matched)
 * - Comparison operators (_gt, _gte, _lt, _lte, _ne, _exists)
 * - Array operators (_all, _in, _nin)
 *
 * @param model - The model object to test against the filter
 * @param filter - The filter criteria, which can be a partial model or include comparison operators
 * @returns `true` if the model matches all filter conditions, `false` otherwise
 *
 * @example
 * // Simple equality
 * modelMatchesGetSomeFilter({ age: 25 }, { age: 25 }) // true
 *
 * @example
 * // Comparison operators
 * modelMatchesGetSomeFilter({ age: 25 }, { age: { _gte: 18 } }) // true
 *
 * @example
 * // Array operators
 * modelMatchesGetSomeFilter({ tags: ['a', 'b'] }, { tags: { _all: ['a'] } }) // true
 *
 * @example
 * // Nested objects
 * modelMatchesGetSomeFilter(
 *   { user: { name: 'John' } },
 *   { user: { name: 'John' } }
 * ) // true
 */
export const modelMatchesGetSomeFilter = <Model extends object>(
  model: Model,
  filter: NonNullable<LoadFunctionArguments<Model>["filter"]> | DeepPartial<Model>
): boolean => {
  const filterEntries = getObjectEntries(filter) as [
    keyof Model,
    NonNullable<LoadFunctionArguments<Model>["filter"]>[keyof Model] | DeepPartial<Model>[keyof DeepPartial<Model>],
  ][];
  return filterEntries.every((entry) => {
    const [filterKey, filterValue] = entry;
    // Null or undefined filter values always match (ignore this filter)
    if (filterValue === undefined || filterValue === null) {
      return true;
    }

    // Simple equality check for primitive values
    const isSimpleFilter = typeof filterValue !== "object";
    if (isSimpleFilter) {
      return model[filterKey] === filterValue;
    }

    const filterValueEntries = getObjectEntries(filterValue);
    const modelValue = model[filterKey];
    const isNested =
      filterValueEntries.every(([key]) => typeof key === "string" && !key.startsWith("_")) &&
      typeof modelValue === "object" &&
      modelValue !== null;
    if (isNested) {
      return modelMatchesGetSomeFilter(modelValue, filterValue);
    }

    const comparativeEntries = filterValueEntries.filter(([key]) => typeof key === "string" && key.startsWith("_"));

    // If there are no comparison operators but filter value is an object,
    // and we're not in the nested case, this means we have a type mismatch
    if (comparativeEntries.length === 0) {
      return false;
    }

    return comparativeEntries.every(([compKey, compValue]) => {
      if (compValue === undefined) return true;
      switch (compKey) {
        case "_exists": {
          const typedCompValue = compValue as boolean;
          return (modelValue !== undefined) === typedCompValue;
        }
        case "_gt": {
          const typedCompValue = compValue as number;
          return typeof modelValue === "number" && modelValue > typedCompValue;
        }
        case "_gte": {
          const typedCompValue = compValue as number;
          return typeof modelValue === "number" && modelValue >= typedCompValue;
        }
        case "_lt": {
          const typedCompValue = compValue as number;
          return typeof modelValue === "number" && modelValue < typedCompValue;
        }
        case "_lte": {
          const typedCompValue = compValue as number;
          return typeof modelValue === "number" && modelValue <= typedCompValue;
        }
        case "_all": {
          const typedCompValue = compValue as unknown[];
          return Array.isArray(modelValue) && typedCompValue.every((entry) => modelValue.includes(entry));
        }
        case "_in": {
          const typedCompValue = compValue as unknown[];
          // MongoDB semantics: if modelValue is an array, match if any element is in compValue
          if (Array.isArray(modelValue)) {
            return modelValue.some((v) => typedCompValue.includes(v));
          }
          return typedCompValue.includes(modelValue);
        }
        case "_nin": {
          const typedCompValue = compValue as unknown[];
          // MongoDB semantics: if modelValue is an array, match only if no element is in compValue
          if (Array.isArray(modelValue)) {
            return !modelValue.some((v) => typedCompValue.includes(v));
          }
          return !typedCompValue.includes(modelValue);
        }
        case "_ne": {
          const typedCompValue = compValue as unknown;
          return typedCompValue !== modelValue;
        }
      }
    });
  });
};

/**
 * Determines whether a model object matches the filter criteria in a getSome request.
 *
 * This function handles both SDK-style filters (using underscore operators like `_exists`, `_ne`)
 * and MongoDB-style filters (using dollar operators like `$exists`, `$ne`).
 *
 * It also supports timestamp-based filtering via `from` and `to` parameters:
 * - `from`: Earliest timestamp (exclusive) - filters models with `createdAt > from`
 * - `to`: Latest timestamp (exclusive) - filters models with `createdAt < to`
 *
 * @param model - The model object to test against the filter
 * @param request - The getSome request object which may contain `filter`, `mdbFilter`, `from`, or `to`
 * @returns `true` if the model matches the filter conditions (or if no filter is specified), `false` otherwise
 *
 * @example
 * // With SDK filter
 * modelMatchesGetSomeRequest(
 *   { sendAt: undefined, delivered: false },
 *   { filter: { sendAt: { _exists: true }, delivered: false } }
 * ) // false
 *
 * @example
 * // With MongoDB filter
 * modelMatchesGetSomeRequest(
 *   { sendAt: undefined, delivered: false },
 *   { mdbFilter: { sendAt: { $exists: true, $ne: "" }, delivered: false } }
 * ) // false
 *
 * @example
 * // With timestamp range
 * modelMatchesGetSomeRequest(
 *   { id: "123", createdAt: new Date("2024-01-15") },
 *   { from: new Date("2024-01-10"), to: new Date("2024-01-20") }
 * ) // true
 *
 * @example
 * // No filter matches everything
 * modelMatchesGetSomeRequest({ id: "123" }, {}) // true
 */
export const modelMatchesGetSomeRequest = <Model extends object>(
  model: Partial<Model>,
  request?: GetSomeRequest<Model>
): boolean => {
  if (!request) return true;

  // Check timestamp filters (from/to) based on createdAt
  if (request.from !== undefined || request.to !== undefined) {
    const modelWithCreatedAt = model as Model & { createdAt?: Date | string | number };
    const createdAt = modelWithCreatedAt.createdAt;

    // If createdAt doesn't exist on the model, timestamp filters don't match
    if (createdAt === undefined || createdAt === null) {
      return false;
    }

    // Convert createdAt to timestamp for comparison
    const createdAtTime = typeof createdAt === "number" ? createdAt : new Date(createdAt).getTime();

    // Check 'from' filter (exclusive - createdAt must be > from)
    if (request.from !== undefined) {
      const fromTime = typeof request.from === "number" ? request.from : new Date(request.from).getTime();

      if (createdAtTime <= fromTime) {
        return false;
      }
    }

    // Check 'to' filter (exclusive - createdAt must be < to)
    if (request.to !== undefined) {
      const toTime = new Date(request.to).getTime();

      if (createdAtTime >= toTime) {
        return false;
      }
    }
  }

  // Check mdbFilter first (MongoDB filter syntax)
  if (request.mdbFilter) {
    return applyMongoFilter(model, request.mdbFilter);
  }

  // Fall back to filter (SDK filter syntax)
  if (request.filter) {
    return modelMatchesGetSomeFilter(model, request.filter);
  }

  // No filter specified - matches all
  return true;
};
